import React, { SetStateAction, useContext } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import getConfig from 'next/config';
import { appAuthContext } from 'context/AuthContext';
import useFormLogic from 'components/Modals/AuthModal/hooks/useFormLogic';
import useWindowWidth from 'utils/useWindowWidth';
import Search from 'components/Header/components/Search';
import Button from 'components/Button';
import SetUserAfterReg from 'components/Header/utils/SetUserAfterReg';

import useClickStreamEvent from 'utils/clickstream/useClickStreamEvent';
import { configuratorContext } from 'context/ConfiguratorContext';
import AiToolkitLink from '../AiToolkitLink/AiToolkitLink';
import styles from './MobileNavItems.module.css';

type MobileNavItemsProps = {
  unanswered: number | null;
  showSearch: boolean;
  setShowSearch: (show: SetStateAction<boolean>) => void;
  setShowAskModal: (show: SetStateAction<boolean>) => void;
};

function MobileNavItems({
  unanswered,
  showSearch,
  setShowSearch,
  setShowAskModal,
}: MobileNavItemsProps) {
  const { sendEvent } = useClickStreamEvent();
  const windowWidth = useWindowWidth();
  const { user } = useContext(appAuthContext);
  const { azFromConfigurator } = useContext(configuratorContext);
  const { openRegistration } = useFormLogic();

  const isUserStudent = user?.data?.attributes?.type === 'student';
  const isStudentGlobalTarget = user?.azTests?.otvety_global_student === 'target';
  const isB2CUser =
    isUserStudent && isStudentGlobalTarget && user?.included?.length === 0;

  const userGrade =
    user?.data && user?.data.type === 'student' && user?.data?.attributes.level;
  const userGradeName = user?.included && user?.included[0]?.attributes?.name;

  const showAiToolkit = azFromConfigurator?.otvety_ai_toolkit?.groupName === 'target';

  const {
    publicRuntimeConfig: { AI_TOOLKIT_URL },
  } = getConfig();

  const regBtnHandler = () => {
    sendEvent({
      eventType: 'click',
      eventInfo: 'otvety_header_reg_btn',
    });
    openRegistration(SetUserAfterReg);
  };

  const sendClickEvent = async (eventInfo: string) => {
    await sendEvent({
      eventType: 'click',
      eventInfo,
    });
  };

  return (
    <div className={styles.mob_nav}>
      <Button
        className={styles.ask__btn}
        text="Задать вопрос"
        onClick={() => setShowAskModal(true)}
      />

      {!user?.data?.attributes && windowWidth < 512 && (
        <div onClick={regBtnHandler} className={`${styles.mob_nav__link} ${styles.bold}`}>
          Зарегистрироваться
        </div>
      )}

      <Link href="/unanswered">
        <a className={styles.mob_nav__link} data-qa-marker="need_answer_btn">
          Нужен ответ
          {unanswered ? (
            <span className={`${styles.counter} ${styles.bounce_in}`}>{unanswered}</span>
          ) : null}
        </a>
      </Link>

      {isB2CUser ? (
        //******* B2C *******//
        <>
          <Link href="/profile">
            <a className={styles.mob_nav__link} data-qa-marker="vk_stickers_btn">
              Мои вопросы
            </a>
          </Link>
          <Link href="/profile/answers">
            <a className={styles.mob_nav__link} data-qa-marker="vk_stickers_btn">
              Мои ответы
            </a>
          </Link>
          {showAiToolkit && (
            <AiToolkitLink
              className={styles.mob_nav__link}
              link={AI_TOOLKIT_URL}
              event_name="otvety-header-ai-toolkit-click"
            />
          )}
        </>
      ) : isStudentGlobalTarget && !isB2CUser ? (
        //******* B2T *******//
        <>
          <a
            className={styles.mob_nav__link}
            href="https://uchi.ru/marathons/students/progresses"
            target="_blank"
            rel="noopener"
            onClick={() => sendClickEvent('otvety_header_marathon_click')}
          >
            Марафон
            {userGrade && userGradeName && (
              <>
                &nbsp;«{userGrade}&nbsp;
                {userGradeName.length > 1 ? `${userGradeName}` : `${userGradeName} класс`}
                »
              </>
            )}
          </a>
          <Link href="/profile">
            <a className={styles.mob_nav__link} data-qa-marker="vk_stickers_btn">
              Мои вопросы
            </a>
          </Link>
          <Link href="/profile/answers">
            <a className={styles.mob_nav__link} data-qa-marker="vk_stickers_btn">
              Мои ответы
            </a>
          </Link>
          {showAiToolkit && (
            <AiToolkitLink
              className={styles.mob_nav__link}
              link={AI_TOOLKIT_URL}
              event_name="otvety-header-ai-toolkit-click"
            />
          )}
        </>
      ) : null}

      <div className={styles.search}>
        {showSearch ? (
          <Search setShowSearch={setShowSearch} />
        ) : (
          <span
            onClick={() => setShowSearch(true)}
            className={`${styles.mob_nav__link} ${styles.df}`}
          >
            <Image
              src="https://static.uchi.ru/files/otvety/search.svg"
              width={20}
              height={20}
              alt="search-icon"
            />
            Поиск
          </span>
        )}
      </div>
    </div>
  );
}

export default MobileNavItems;
